import { z } from "zod";

import { TeamsUserRoles } from "../../hooks/useAccountTypes";
import type { BillingDetails } from "../../hooks/useCheckout";
import { PaymentType, PaymentTypes } from "../../lib/ChargebeeAdapter";
import { SubscriptionPeriod } from "../../types";
import type {
  CreditCardInputError,
  CreditCardInputValue,
} from "../CreditCardInput/CreditCardInputTypes";

export const COUNTRIES_WITH_MANDATORY_STATE_SELECTOR = ["US", "CA", "IN", "BR"];
export const MANDATORY_BILLING_DATA_COUNTRIES = ["US", "BR", "CA", "IN"];
export const MANDATORY_BILLING_DATA_FIELDS: (keyof BillingDetails)[] = [
  "company",
  "zip",
  "city",
  "countryCode",
  "line1",
];

export const mapChargebeeErrorMessages = (error: string) => {
  if (error === "CVC Declined" || error.includes("incorrect_cvc")) {
    // This error is shown as a tooltip on the card input field
    return undefined;
  }
  if (
    error === "Refused" ||
    error.includes("Your card was declined") ||
    error.includes("Your card has been declined")
  ) {
    return {
      title: "Payment declined",
      description:
        "There is an issue with your card. Please try again or use a different card. If the issue persists, contact your bank.",
    };
  }
  if (error === "Not enough balance" || error.includes("Your card has insufficient funds")) {
    return {
      title: "Insufficient funds",
      description: "Please check your balance or try a different card.",
    };
  }
  if (
    error === "3D Not Authenticated" ||
    error.includes("We are unable to authenticate your payment method")
  ) {
    return {
      title: "Payment verification failed",
      description:
        "Please try again and follow your bank's verification instructions. If the issue persists, try a different card or contact your bank.",
    };
  }
  return {
    title: "Payment Error",
    description:
      "Please check your details and try again. If the issue persists, try a different card or contact your bank.",
  };
};

const mapCreditCardErrorMessages = (error: CreditCardInputError) => {
  switch (error.errorCode) {
    case "card_number_incomplete":
      return "Incomplete card number";
    case "card_number_invalid":
      return "Invalid card number";
    case "card_cvv_invalid":
      return "Invalid CVV number";
    default:
      return error.message;
  }
};

export const schema = z
  .object({
    editorSeatCount: z.number().min(1).max(50),
    inviteEmails: z.array(z.string()),
    role: z.enum([TeamsUserRoles.Editor, TeamsUserRoles.Contributor]).optional(),
    paymentType: z.enum(PaymentTypes),
    card: z.custom<CreditCardInputValue>().optional(),
    period: z.nativeEnum(SubscriptionPeriod),
    company: z.string().optional(),
    countryCode: z.string().optional(),
    stateCode: z.string().optional(),
    city: z.string().optional(),
    zip: z.string().optional(),
    line1: z.string().optional(),
    line2: z.string().optional(),
    vatNumber: z.string().optional(),
    couponCode: z.string().optional(),
    migrationConsent: z.boolean().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.paymentType !== PaymentType.Card) {
      return;
    }

    if (!val.card) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        type: "string",
        minimum: 1,
        inclusive: true,
        exact: false,
        message: "Required",
        path: ["card"],
      });

      return;
    }

    if (val.card.error) {
      ctx.addIssue({
        code: "custom",
        message: mapCreditCardErrorMessages(val.card?.error),
        path: ["card"],
      });

      return;
    }
  })
  .superRefine((val, ctx) => {
    if (!val.countryCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        type: "string",
        minimum: 1,
        inclusive: true,
        exact: false,
        message: "Required",
        path: ["countryCode"],
      });
    }
  })
  .superRefine((val, ctx) => {
    const country = val.countryCode;
    if (country && COUNTRIES_WITH_MANDATORY_STATE_SELECTOR.includes(country) && !val.stateCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: ["stateCode"],
      });
    }
  })
  .superRefine((val, ctx) => {
    const country = val.countryCode;
    if ((country && MANDATORY_BILLING_DATA_COUNTRIES.includes(country)) || val.vatNumber) {
      const missingBillingDetailsFields = MANDATORY_BILLING_DATA_FIELDS.filter(
        (fieldName) => !val[fieldName]
      );
      missingBillingDetailsFields.forEach((fieldName) => {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Required",
          path: [fieldName],
        });
      });
    }
  });

export type CheckoutFormData = z.infer<typeof schema>;
